import { defineStore } from 'pinia'
import {
  transformOutgoingDevice,
  transformIncomingDevice,
  type TransformedDevice,
} from '@/utils/dataTransformers'
import { useNetworkStore } from '@/stores/network/store'
import { useOfficeDeskStore } from '@/stores/officeDesk/store'
import * as Api from './api'
import {
  callWithMeta,
  createMeta,
  getMetaSuccess,
  waitFor,
  type Meta,
} from '@/utils/store'
import { useDevicesStore } from '@/stores/devices/store'
import { useExtensionsStore } from '@/stores/extensions/store'
import { useExtensionStore } from '@/stores/extension/store'
import { useOfficeDesksStore } from '@/stores/officeDesks/store'
import { useAuthStore } from '@/stores/auth/store'

type State = {
  deviceData?: TransformedDevice
  meta: Meta<{
    deviceData: { id: string | number } | undefined
    load: { id: string | number }
    create: undefined
    update: { id: string | number }
    delete: { id: string | number }
    updateSoftkeys: { id: string | number }
    updateAllowedIps: { id: string | number }
    unlockProvisioning: { id: string | number }
  }>
}

const getDefaultState = (): State => ({
  deviceData: undefined,
  meta: createMeta([
    'deviceData',
    'load',
    'create',
    'update',
    'delete',
    'updateSoftkeys',
    'updateAllowedIps',
    'unlockProvisioning',
  ]),
})

export const useDeviceStore = defineStore('device', {
  state: getDefaultState,

  getters: {
    get: (state) => state.deviceData,
  },

  actions: {
    RESET() {
      this.deviceData = getDefaultState().deviceData
      this.meta.deviceData = getDefaultState().meta.deviceData
    },

    async load(id: string | number, { useCache = true } = {}) {
      const hasCached =
        ['pending', 'success'].includes(this.meta.deviceData.status) &&
        Number(id) === Number(this.meta.deviceData.params?.id)

      if (useCache && hasCached) {
        await waitFor(() => this.meta.deviceData.status !== 'pending')

        return
      }

      const params = { id }

      await callWithMeta({
        store: this,
        metaFields: { load: params, deviceData: params },
        apiCall: async () => await Api.loadDevice(id),
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('deviceData')) {
            this.deviceData = transformIncomingDevice(result.payload)
          }
        },
        onError: ({ result }) => {
          useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
        },
      })
    },

    async reload() {
      if (
        this.meta.deviceData.status !== 'initial' &&
        this.meta.deviceData.params
      ) {
        await this.load(this.meta.deviceData.params.id, { useCache: false })
      }
    },

    async create(data: any) {
      const { deviceData, officeDeskData } = transformOutgoingDevice(data)

      const { result } = await callWithMeta({
        store: this,
        metaFields: { create: undefined, deviceData: undefined },
        apiCall: async () => {
          const result = await Api.createDevice(deviceData)

          if (result.status === 'success') {
            const newDevice = result.payload

            if (
              officeDeskData &&
              newDevice.officeDesk &&
              newDevice.officeDesk.id
            ) {
              result.payload.officeDesk = await useOfficeDeskStore().update({
                id: newDevice.officeDesk.id,
                data: officeDeskData,
              })
            }
          }

          return result
        },
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('create')) {
            useExtensionsStore().load({ useCache: false })

            useExtensionStore().reload()

            useDevicesStore().load({ useCache: false })

            setTimeout(async () => {
              await useOfficeDesksStore().load({ useCache: false })

              useOfficeDeskStore().reload()
            })
          }

          if (isLatest('deviceData')) {
            this.deviceData = transformIncomingDevice(result.payload)

            this.meta.deviceData = getMetaSuccess({
              id: result.payload.id,
            })
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async update({ id, data }: any) {
      const { deviceData, officeDeskData } = transformOutgoingDevice(data)
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { update: params, deviceData: params },
        apiCall: async () => {
          const result = await Api.updateDevice(id, deviceData)

          if (result.status === 'success') {
            const newDevice = result.payload

            if (
              officeDeskData &&
              newDevice.officeDesk &&
              newDevice.officeDesk.id &&
              !useAuthStore().isOfficeDesk
            ) {
              await useOfficeDeskStore().update({
                id: newDevice.officeDesk.id,
                data: officeDeskData,
              })
            }
          }

          return result
        },
        onSuccess: ({ isLatest, result }) => {
          if (isLatest('update')) {
            useExtensionsStore().load({ useCache: false })

            useExtensionStore().reload()

            useDevicesStore().load({ useCache: false })

            setTimeout(async () => {
              await useOfficeDesksStore().load({ useCache: false })

              useOfficeDeskStore().reload()
            })
          }

          if (isLatest('deviceData')) {
            this.deviceData = transformIncomingDevice(result.payload)
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async delete(id: string | number) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { delete: params, deviceData: params },
        apiCall: async () => await Api.deleteDevice(id),
        onSuccess: ({ isLatest }) => {
          if (isLatest('delete')) {
            useExtensionsStore().load({ useCache: false })

            useExtensionStore().reload()

            useDevicesStore().load({ useCache: false })

            setTimeout(async () => {
              await useOfficeDesksStore().load({ useCache: false })

              useOfficeDeskStore().reload()
            })
          }

          if (isLatest('deviceData')) {
            this.RESET()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async updateSoftkeys(
      id: string | number,
      softkeys: Omit<Api.Softkey, 'id'>[]
    ) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { updateSoftkeys: params, deviceData: params },
        apiCall: async () => await Api.updateSoftkeys(id, softkeys),
        onSuccess: ({ isLatest }) => {
          if (isLatest('updateSoftkeys')) {
            useDevicesStore().load({ useCache: false })
          }

          if (isLatest('deviceData')) {
            this.reload()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async updateAllowedIps(
      id: string | number,
      allowedIps: Partial<NonNullable<Api.Device['trustedIps']>[number]>[]
    ) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { updateAllowedIps: params, deviceData: params },
        apiCall: async () => await Api.updateAllowedIps(id, allowedIps),
        onSuccess: ({ isLatest }) => {
          if (isLatest('deviceData')) {
            this.reload()
          }
        },
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },

    async unlockProvisioning(id: number | string) {
      const params = { id }

      const { result } = await callWithMeta({
        store: this,
        metaFields: { unlockProvisioning: params },
        apiCall: async () => await Api.unlockProvisioning(id),
      })

      if (result.status === 'error') {
        throw result.payload
      }
    },
  },
})
